import React, { useContext, useEffect, useState } from "react";
import { SaasTherapistService } from "src/services/SaasTherapistService";
import { toast } from "react-toastify";
import UserContext from "src/context/UserContext";
import Spinner from "src/common/spinner/spinner";
import { UserDetails } from "src/models/User";

interface JotformSaasProps {
  clientId: string;
}

interface JotformData {
  formName: string;
  resultLink: string;
  submissionDate: string;
  score_GAD_7: number;
  score_PHQ_9: number;
  score_AUDIT: number;
  score_DAST: number;
}

interface EnabledForm {
  _id: string;
  form_id: string;
  title_form: string;
  status: string;
}

const JotformSaas: React.FC<JotformSaasProps> = ({ clientId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [jotformData, setJotformData] = useState<JotformData[]>([]);
  const [enabledForms, setEnabledForms] = useState<EnabledForm[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<string>('');
  const [generatedLink, setGeneratedLink] = useState<string>('');
  const [user] = useContext(UserContext);

  useEffect(() => {
    if (user?._id) {
      fetchJotformData();
      fetchEnabledForms();
    } else {
      toast.error("User information not available", {
        position: toast.POSITION.TOP_RIGHT,
        className: "foo-bar",
      });
      setIsLoading(false);
    }
  }, [clientId, user]);

  const fetchJotformData = async () => {
    if (!user?._id) return;
    
    setIsLoading(true);
    try {
      const response = await SaasTherapistService.getJotFormByClientId(user._id, clientId);
      if (response.success) {
        setJotformData(response.data);
      } else {
        toast.error(response.error || "Error loading Jotform data", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
      }
    } catch (error) {
      toast.error("Failed to fetch Jotform data", {
        position: toast.POSITION.TOP_RIGHT,
        className: "foo-bar",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEnabledForms = async () => {
    try {
      const response = await SaasTherapistService.getEnabledForms();
      if (response.success) {
        setEnabledForms(response.data);
      } else {
        toast.error(response.error || "Error loading enabled forms", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
      }
    } catch (error) {
      toast.error("Failed to fetch enabled forms", {
        position: toast.POSITION.TOP_RIGHT,
        className: "foo-bar",
      });
    }
  };

  const generateFormLink = () => {
    if (!selectedFormId || !user?._id) return;
    const link = `https://form.jotform.com/${selectedFormId}?hash_therapist=${user._id}&hash_clientid=${clientId}`;
    setGeneratedLink(link);
  };

  const copyToClipboard = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink).then(() => {
        toast.success("Link copied to clipboard!", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
      });
    }
  };

  const sendEmailInvitation = async () => {
    if (!generatedLink || !clientId) return;
    
    try {
      setIsSendingEmail(true);
      const response = await SaasTherapistService.sendJotformInvitation({
        clientId,
        formLink: generatedLink
      });
      
      if (response.success) {
        toast.success("Form invitation sent successfully!", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
      } else {
        toast.error(response.error || "Failed to send form invitation", {
          position: toast.POSITION.TOP_RIGHT,
          className: "foo-bar",
        });
      }
    } catch (error) {
      toast.error("Failed to send form invitation", {
        position: toast.POSITION.TOP_RIGHT,
        className: "foo-bar",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
        <Spinner className="bouncer" />
      </div>
    );
  }
  
  // Helper function to give score colors based on value ranges
  const getScoreColor = (score: number, type: string) => {
    if (score === 0) return '#6c757d';
    
    if (type === 'PHQ_9') {
      if (score < 5) return '#28a745'; // Minimal
      if (score < 10) return '#ffc107'; // Mild
      if (score < 15) return '#fd7e14'; // Moderate
      if (score < 20) return '#dc3545'; // Moderately Severe
      return '#6f42c1'; // Severe
    }
    
    if (type === 'GAD_7') {
      if (score < 5) return '#28a745'; // Minimal
      if (score < 10) return '#ffc107'; // Mild
      if (score < 15) return '#fd7e14'; // Moderate
      return '#dc3545'; // Severe
    }
    
    if (type === 'AUDIT') {
      if (score < 8) return '#28a745'; // Low Risk
      if (score < 16) return '#ffc107'; // Medium Risk
      if (score < 20) return '#fd7e14'; // High Risk
      return '#dc3545'; // Very High Risk
    }
    
    if (type === 'DAST') {
      if (score < 3) return '#28a745'; // Low
      if (score < 6) return '#ffc107'; // Moderate
      return '#dc3545'; // Substantial
    }
    
    return '#6c757d'; // Default gray for unknown types
  }

  return (
    <div className="animate__animated animate__fadeIn">
      {/* Link Generation Section */}
      <div className="mb-5" style={{
        background: 'linear-gradient(145deg, #f8f9fa, #ffffff)',
        padding: '30px',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #e9ecef'
      }}>
        <h4 className="mb-4" style={{ 
          color: '#2c3e50', 
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span style={{
            background: '#ff6634',
            color: 'white',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '5px'
          }}>
            <i className="bi bi-link-45deg"></i>
          </span>
          Form Link Generator
        </h4>
        <div className="row g-3 align-items-end">
          <div className="col-md-8">
            <select
              className="form-select form-select-lg"
              value={selectedFormId}
              onChange={(e) => setSelectedFormId(e.target.value)}
              style={{ 
                height: '50px', 
                fontSize: '16px',
                boxShadow: 'none',
                border: '1px solid #ced4da',
                borderRadius: '8px',
                paddingLeft: '16px',
                transition: 'all 0.3s ease-in-out'
              }}
            >
              <option value="">Select a form...</option>
              {enabledForms.map((form) => (
                <option key={form._id} value={form.form_id}>
                  {form.title_form}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <button
              className="btn btn-primary w-100"
              onClick={generateFormLink}
              disabled={!selectedFormId}
              style={{
                height: '50px',
                background: '#ff6634',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                boxShadow: '0 4px 12px rgba(255, 102, 52, 0.25)',
                transition: 'all 0.3s ease'
              }}
            >
              <i className="bi bi-magic me-1"></i> Generate Link
            </button>
          </div>
        </div>
        {generatedLink && (
          <div className="mt-3">
            <div className="input-group">
              <input
                type="text"
                className="form-control form-control-lg"
                value={generatedLink}
                readOnly
                style={{ 
                  height: '50px', 
                  fontSize: '16px',
                  border: '1px solid #ced4da',
                  borderRadius: '8px 0 0 8px',
                  backgroundColor: '#f8f9fa',
                  paddingLeft: '16px',
                  color: '#495057',
                  fontFamily: 'monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              />
              <button
                className="btn btn-outline-primary"
                onClick={copyToClipboard}
                style={{
                  height: '50px',
                  borderColor: '#ff6634',
                  color: '#ff6634',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  borderRadius: '0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <i className="bi bi-clipboard"></i> Copy
              </button>
              <button
                className="btn btn-primary"
                onClick={sendEmailInvitation}
                disabled={isSendingEmail}
                style={{
                  height: '50px',
                  background: '#2979ff',
                  border: 'none',
                  borderRadius: '0 8px 8px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 4px 12px rgba(41, 121, 255, 0.25)',
                  transition: 'all 0.3s ease'
                }}
              >
                {isSendingEmail ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="bi bi-envelope"></i>
                    Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Visual divider with label */}
      <div className="d-flex align-items-center mb-4">
        <div style={{ flex: 1, height: '2px', background: 'linear-gradient(to right, transparent, #dee2e6)' }}></div>
        <div className="px-4" style={{ 
          color: '#495057', 
          fontWeight: 'bold',
          letterSpacing: '1px',
          fontSize: '14px',
          backgroundColor: '#f8f9fa',
          padding: '8px 16px',
          borderRadius: '20px',
          border: '1px solid #dee2e6'
        }}>FORM SUBMISSIONS</div>
        <div style={{ flex: 1, height: '2px', background: 'linear-gradient(to left, transparent, #dee2e6)' }}></div>
      </div>

      {/* Results Table Section */}
      <div className="table-responsive" style={{ 
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        padding: '10px',
        border: '1px solid #e9ecef',
        overflow: 'hidden'
      }}>
        <table className="table table-hover mb-0" style={{
          borderCollapse: 'separate',
          borderSpacing: '0',
        }}>
          <thead>
            <tr style={{
              background: '#f8f9fa',
              borderBottom: '2px solid #dee2e6',
            }}>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057' }}>Form Name</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057' }}>Submission Date</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057', textAlign: 'center' }}>Score PHQ 9</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057', textAlign: 'center' }}>Score GAD 7</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057', textAlign: 'center' }}>Score AUDIT</th>
              <th style={{ padding: '15px', fontSize: '14px', fontWeight: '600', color: '#495057', textAlign: 'center' }}>Score DAST</th>
            </tr>
          </thead>
          <tbody>
            {jotformData.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center">No forms available</td>
              </tr>
            ) : (
              jotformData.map((form, index) => (
                <tr key={index} style={{
                    transition: 'all 0.2s ease',
                    backgroundColor: index % 2 === 0 ? 'white' : '#fafafa'
                  }}>
                  <td style={{ padding: '15px', verticalAlign: 'middle', fontWeight: '500' }}>{form.formName}</td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', color: '#6c757d' }}>{formatDate(form.submissionDate)}</td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center' }}>
                    {form.score_PHQ_9 > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                        <span style={{ 
                          fontWeight: '600', 
                          fontSize: '18px', 
                          color: getScoreColor(form.score_PHQ_9, 'PHQ_9')
                        }}>
                          {form.score_PHQ_9}
                        </span>
                        <span
                          className="view-btn"
                          onClick={() => window.open(form.resultLink, "_blank")}
                          style={{
                            color: '#ff6634',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            textDecoration: 'underline',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px'
                          }}
                        >
                          <i className="bi bi-eye-fill" style={{ fontSize: '10px' }}></i> View
                        </span>
                      </div>
                    ) : (
                      <span style={{ color: '#6c757d', fontSize: '16px' }}>0</span>
                    )}
                  </td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center' }}>
                    {form.score_GAD_7 > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                        <span style={{ 
                          fontWeight: '600', 
                          fontSize: '18px', 
                          color: getScoreColor(form.score_GAD_7, 'GAD_7')
                        }}>
                          {form.score_GAD_7}
                        </span>
                        <span
                          className="view-btn"
                          onClick={() => window.open(form.resultLink, "_blank")}
                          style={{
                            color: '#ff6634',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            textDecoration: 'underline',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px'
                          }}
                        >
                          <i className="bi bi-eye-fill" style={{ fontSize: '10px' }}></i> View
                        </span>
                      </div>
                    ) : (
                      <span style={{ color: '#6c757d', fontSize: '16px' }}>0</span>
                    )}
                  </td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center' }}>
                    {form.score_AUDIT > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                        <span style={{ 
                          fontWeight: '600', 
                          fontSize: '18px', 
                          color: getScoreColor(form.score_AUDIT, 'AUDIT')
                        }}>
                          {form.score_AUDIT}
                        </span>
                        <span
                          className="view-btn"
                          onClick={() => window.open(form.resultLink, "_blank")}
                          style={{
                            color: '#ff6634',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            textDecoration: 'underline',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px'
                          }}
                        >
                          <i className="bi bi-eye-fill" style={{ fontSize: '10px' }}></i> View
                        </span>
                      </div>
                    ) : (
                      <span style={{ color: '#6c757d', fontSize: '16px' }}>0</span>
                    )}
                  </td>
                  <td style={{ padding: '15px', verticalAlign: 'middle', textAlign: 'center' }}>
                    {form.score_DAST > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                        <span style={{ 
                          fontWeight: '600', 
                          fontSize: '18px', 
                          color: getScoreColor(form.score_DAST, 'DAST')
                        }}>
                          {form.score_DAST}
                        </span>
                        <span
                          className="view-btn"
                          onClick={() => window.open(form.resultLink, "_blank")}
                          style={{
                            color: '#ff6634',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            textDecoration: 'underline',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px'
                          }}
                        >
                          <i className="bi bi-eye-fill" style={{ fontSize: '10px' }}></i> View
                        </span>
                      </div>
                    ) : (
                      <span style={{ color: '#6c757d', fontSize: '16px' }}>0</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JotformSaas;
